import { useEffect, useState } from 'react';
import API from '../api';

// ── 데이터베이스 백업/복구 화면 ─────────────────────────────────
// 최고관리자 전용 메뉴. "백업" 탭에는 실제 백업 시점 목록이, "복구" 탭에는 지금까지 실행된
// 복구 이력이 표시됩니다(생산관리의 전체/자체생산/주문생산 탭과 동일한 스타일).
//  - 2026-08 개편 — 목록의 한 줄을 클릭하면 그 줄에 대해 실행할 수 있는 작업(복구/삭제)이
//    작은 모달창으로 뜬다. 예전에는 "작업" 열에 버튼이 바로 표시되고 별도의 헤더 [복구]
//    버튼도 있었는데, 백업 대상 메뉴가 계속 늘어나면서(19종) 표 자체가 지나치게 넓어져
//    "작업" 열까지 넣으면 가로 스크롤이 심해졌다. 그래서 "작업" 열은 표에서 아예 없애고,
//    행을 클릭했을 때만 모달로 복구/삭제 버튼을 보여주는 방식으로 바꿨다.
//  - 우측상단 [백업] 버튼은 지금 시점 기준으로 새 백업을 실행합니다(대상 메뉴 선택, 기본 전체).
//  - 매일 새벽 3시30분 자동(시스템) 백업이 별도로 실행되며, "실행" 열에 시스템백업/사용자백업/
//    사용자복구로 구분되어 표시됩니다.

const cellHead = {
  padding:'10px 8px', background:'#f8fafc', fontSize:13, fontWeight:600,
  color:'#555', border:'1px solid #e5e7eb', textAlign:'center', whiteSpace:'nowrap'
};
const cellBody = {
  padding:'10px 8px', fontSize:13, border:'1px solid #e5e7eb',
  textAlign:'center', verticalAlign:'middle'
};
// 2026-08 요청 — 메뉴별 O/X 열(공지사항, 포스트잇, 일정관리 … 사내메신저 등)은 메뉴 종류가
// 19개까지 늘어나 표가 너무 넓어졌다. 실행/일자/시간/실행자 같은 고정 열은 그대로 두고,
// 이 메뉴별 열들만 좌우 여백을 8px→3px로 줄여서 표 전체 폭을 줄인다.
const menuCellHead = { ...cellHead, padding:'10px 3px' };
const menuCellBody = { ...cellBody, padding:'10px 3px' };

export default function BackupRestore() {
  const [tab, setTab] = useState('backup'); // 'backup' | 'restore'
  const [menus, setMenus] = useState([]);        // [{key,label}]
  const [logs, setLogs] = useState([]);           // backup_logs 전체
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');

  // 2026-08 개편 — 클릭한 행에 대해 복구/삭제 버튼을 모달로 보여준다("작업" 열 대체).
  const [actionRow, setActionRow] = useState(null);

  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupSel, setBackupSel] = useState([]);

  const [restoreTarget, setRestoreTarget] = useState(null); // 복구 대상 백업 row
  const [restoreSel, setRestoreSel] = useState([]);

  const [showHistory, setShowHistory] = useState(false); // 전체 이력(백업+복구 통합) 모달

  const loadMenus = () => API.get('/backup/menus').then(r => setMenus(r.data)).catch(()=>{});
  const loadLogs  = () => API.get('/backup/list').then(r => setLogs(r.data)).catch(()=>{});

  useEffect(() => { loadMenus(); loadLogs(); }, []);

  const showMsg = (type, text) => {
    setMsgType(type); setMsg(text);
    setTimeout(() => setMsg(''), 4000);
  };

  const backupLogs  = logs.filter(l => l.action === 'backup');
  const restoreLogs = logs.filter(l => l.action === 'restore');

  const execLabel = (row) => {
    if (row.action === 'restore') return '사용자복구';
    return row.backup_type === 'system' ? '시스템백업' : '사용자백업';
  };

  // 'YYYY-MM-DD HH:MM:SS' → { date:'YYYY-MM-DD', time:'HH:MM:SS' }
  const splitDT = (str) => {
    if (!str) return { date:'-', time:'-' };
    const s = String(str).trim();
    const m = s.match(/(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/);
    if (m) return { date: m[1], time: m[2] };
    return { date: s, time:'-' };
  };

  const openBackupModal = () => {
    setBackupSel(menus.map(m => m.key)); // 기본값: 전체 선택
    setShowBackupModal(true);
  };

  const runBackup = async () => {
    if (!backupSel.length) return showMsg('error', '백업할 메뉴를 하나 이상 선택해주세요.');
    setLoading(true);
    try {
      await API.post('/backup', { menus: backupSel });
      showMsg('success', '백업이 완료되었습니다.');
      setShowBackupModal(false);
      loadLogs();
    } catch (err) {
      showMsg('error', err?.response?.data?.message || '백업 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 2026-08 개편 — 행을 클릭하면(탭 구분 없이) 그 행에 대한 작업 모달을 연다. 복구는
  // 백업 이력에서만 의미가 있으므로 모달 안에서 row.action==='backup'일 때만 버튼을 보여준다.
  const clickRow = (row) => setActionRow(row);

  const openRestoreModal = (row) => {
    setRestoreTarget(row);
    setRestoreSel(row.menus); // 기본값: 그 백업 시점에 포함됐던 메뉴 전체
  };

  const runRestore = async () => {
    if (!restoreTarget) return;
    if (!restoreSel.length) return showMsg('error', '복구할 메뉴를 하나 이상 선택해주세요.');
    const ok = window.confirm(
      `${restoreTarget.file_path ? splitDT(restoreTarget.created_at).date : ''} 시점으로 선택한 메뉴의 데이터를 복구합니다.\n` +
      `선택하지 않은 메뉴의 데이터는 그대로 유지됩니다.\n\n` +
      `복구 후에는 되돌릴 수 없습니다. 계속하시겠습니까?`
    );
    if (!ok) return;
    setLoading(true);
    try {
      await API.post(`/backup/${restoreTarget.id}/restore`, { menus: restoreSel });
      showMsg('success', '복구가 완료되었습니다.');
      setRestoreTarget(null);
      loadLogs();
    } catch (err) {
      showMsg('error', err?.response?.data?.message || '복구 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMenuKey = (list, setList, key) => {
    setList(list.includes(key) ? list.filter(k => k !== key) : [...list, key]);
  };

  // 백업/복구 이력 한 건 삭제. 백업 이력이면 서버가 실제 파일도 함께 지운다.
  const deleteRow = async (row) => {
    const ok = window.confirm(
      row.action === 'backup'
        ? '이 백업을 삭제하면 저장된 백업 파일도 함께 삭제되어 복구할 수 없게 됩니다.\n계속하시겠습니까?'
        : '이 복구 이력을 삭제하시겠습니까?'
    );
    if (!ok) return;
    try {
      await API.delete(`/backup/${row.id}`);
      loadLogs();
      showMsg('success', '삭제되었습니다.');
    } catch (err) {
      showMsg('error', err?.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  const rows = tab === 'backup' ? backupLogs : restoreLogs;

  return (
    <div style={{ padding:24, maxWidth:1690 }}>

      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>🗄️ 데이터베이스 백업/복구</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>
            메뉴별로 데이터를 백업하고, 원하는 시점으로 선택 복구할 수 있습니다. 목록의 한 줄을
            클릭하면 복구/삭제 버튼이 있는 창이 뜹니다.
          </p>
        </div>
        <div className="hdr-actions" style={{ display:'flex', gap:8 }}>
          <button onClick={() => setShowHistory(true)}
            style={{ background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            📜 히스토리
          </button>
          <button onClick={openBackupModal}
            style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            💾 백업
          </button>
        </div>
      </div>

      {msg && (
        <div style={{ background:msgType==='success'?'#d1fae5':'#fee2e2',
          color:msgType==='success'?'#065f46':'#dc2626',
          padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>
      )}

      {/* 탭 (생산관리 전체/자체생산/주문생산과 동일한 스타일) */}
      <div style={{ display:'flex', borderBottom:'1px solid #e5e7eb', marginBottom:16 }}>
        {[{ key:'backup', label:'백업' }, { key:'restore', label:'복구' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding:'10px 24px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
              borderBottom: tab===t.key?'2px solid #185FA5':'2px solid transparent',
              background:'none', color:tab===t.key?'#185FA5':'#888', marginBottom:-2 }}>
            {t.label}
            <span style={{ fontSize:11, marginLeft:6, background:tab===t.key?'#185FA5':'#e5e7eb',
              color:tab===t.key?'#fff':'#888', padding:'1px 7px', borderRadius:20 }}>
              {t.key==='backup' ? backupLogs.length : restoreLogs.length}
            </span>
          </button>
        ))}
      </div>

      {/* 목록 */}
      <div style={{ overflowX:'auto', border:'1px solid #e5e7eb', borderRadius:8 }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={cellHead}>실행</th>
              <th style={cellHead}>일자</th>
              <th style={cellHead}>시간</th>
              {menus.map(m => (
                <th key={m.key} style={{ ...menuCellHead, minWidth:64 }}>{m.label}</th>
              ))}
              <th style={cellHead}>실행자</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4 + menus.length} style={{ ...cellBody, padding:'28px 8px', color:'#999' }}>
                {tab==='backup' ? '백업 이력이 없습니다.' : '복구 이력이 없습니다.'}
              </td></tr>
            )}
            {rows.map(row => {
              const { date, time } = splitDT(row.created_at);
              return (
                <tr key={row.id}
                  onClick={() => clickRow(row)}
                  style={{ background:'#fff', cursor:'pointer' }}>
                  <td style={cellBody}>{execLabel(row)}</td>
                  <td style={cellBody}>{date}</td>
                  <td style={cellBody}>{time}</td>
                  {menus.map(m => (
                    <td key={m.key} style={{ ...menuCellBody,
                      color: row.menus.includes(m.key) ? '#185FA5' : '#ccc', fontWeight:700 }}>
                      {row.menus.includes(m.key) ? 'O' : 'X'}
                    </td>
                  ))}
                  <td style={cellBody}>{row.created_by || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 2026-08 개편 — 목록의 한 줄을 클릭했을 때 뜨는 작업 모달(복구/삭제) */}
      {actionRow && (
        <RowActionModal
          row={actionRow}
          execLabel={execLabel}
          splitDT={splitDT}
          onRestore={() => { const r = actionRow; setActionRow(null); openRestoreModal(r); }}
          onDelete={async () => { await deleteRow(actionRow); setActionRow(null); }}
          onClose={() => setActionRow(null)}
        />
      )}

      {/* 백업 실행 모달 */}
      {showBackupModal && (
        <MenuSelectModal
          title="데이터 백업"
          desc="선택한 메뉴의 데이터를 지금 시점 기준으로 백업합니다."
          menus={menus}
          selected={backupSel}
          onToggle={(key) => toggleMenuKey(backupSel, setBackupSel, key)}
          onSelectAll={() => setBackupSel(menus.map(m => m.key))}
          onClose={() => setShowBackupModal(false)}
          onConfirm={runBackup}
          confirmLabel="백업 실행"
          loading={loading}
        />
      )}

      {/* 복구 실행 모달 */}
      {restoreTarget && (
        <MenuSelectModal
          title="데이터 복구"
          desc={`${splitDT(restoreTarget.created_at).date} ${splitDT(restoreTarget.created_at).time} 시점으로 선택한 메뉴만 복구합니다. 선택하지 않은 메뉴는 현재 데이터가 그대로 유지됩니다.`}
          menus={menus.filter(m => restoreTarget.menus.includes(m.key))}
          selected={restoreSel}
          onToggle={(key) => toggleMenuKey(restoreSel, setRestoreSel, key)}
          onSelectAll={() => setRestoreSel(restoreTarget.menus)}
          onClose={() => setRestoreTarget(null)}
          onConfirm={runRestore}
          confirmLabel="복구 실행"
          confirmColor="#dc2626"
          loading={loading}
        />
      )}

      {/* 전체 이력(백업+복구 통합) 모달 — 탭으로 나뉘어 보이는 목록과 별개로, 지금까지의
          모든 백업/복구 기록을 한 화면에서 시간순으로 조회하고 정리(삭제)할 수 있는 곳 */}
      {showHistory && (
        <HistoryModal
          logs={logs}
          menus={menus}
          execLabel={execLabel}
          splitDT={splitDT}
          onDelete={deleteRow}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

// 2026-08 신설 — 목록에서 행을 클릭했을 때 뜨는 작업 모달. 백업 이력이면 "이 시점으로 복구"와
// "삭제" 두 버튼을, 복구 이력이면 "삭제" 버튼만 보여준다(복구 이력 자체를 다시 복구할 수는 없음).
function RowActionModal({ row, execLabel, splitDT, onRestore, onDelete, onClose }) {
  const { date, time } = splitDT(row.created_at);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, width:360, maxWidth:'90vw', overflow:'hidden' }}>
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #eee' }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>{execLabel(row)}</h3>
          <p style={{ margin:'6px 0 0', fontSize:12, color:'#888' }}>
            {date} {time} · 실행자 {row.created_by || '-'}
          </p>
        </div>
        <div style={{ padding:'16px 20px', display:'flex', flexDirection:'column', gap:8 }}>
          {row.action === 'backup' && (
            <button onClick={onRestore}
              style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 14px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              ♻️ 이 시점으로 복구
            </button>
          )}
          <button onClick={onDelete}
            style={{ background:'#fff', color:'#dc2626', border:'1px solid #f5c2c2', borderRadius:8,
              padding:'10px 14px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            🗑 삭제
          </button>
        </div>
        <div style={{ padding:'14px 20px', borderTop:'1px solid #eee', display:'flex', justifyContent:'flex-end' }}>
          <button onClick={onClose}
            style={{ background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// 백업+복구 통합 이력 모달 — 조회와 삭제(정리)만 담당 (실제 복구 실행은 백업 탭에서)
//
// 2026-08 요청 — "히스토리를 누르면 수동으로 백업 및 복구한 내역만 기록" — 매일 새벽 3시30분
// 자동 실행되는 시스템 백업(backup_type==='system')은 사용자가 직접 실행한 게 아니라서 여기서는
// 제외한다. 복구는 애초에 자동 실행되는 경로가 없어(자동 복구 기능 자체가 없음) 항상 전부
// 사용자가 수동으로 실행한 것이므로 그대로 둔다. 실행자 표기는 backupRestore.js formatUser가
// 이미 "부서 직급 이름(아이디)" 형태로 만들어 내려주므로 여기서는 그대로 표시만 하면 된다.
// 2026-08 요청 — 히스토리 표에서 메뉴별 O/X 열을 전부 없애고, 대신 "항목" 열 하나에 버튼을
// 두어(항목 보기 (N개)) 클릭하면 그 백업/복구 건에 실제로 포함된 항목만 모달로 보여주도록
// 개편했다. 열 구성은 요청 원문 순서 그대로: 실행, 일자, 시간, 실행자, 항목, 삭제.
function HistoryModal({ logs, menus, execLabel, splitDT, onDelete, onClose }) {
  const manualLogs = logs.filter(l => !(l.action === 'backup' && l.backup_type === 'system'));
  const [itemsRow, setItemsRow] = useState(null); // 항목 보기 버튼으로 클릭한 행
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, width:'92vw', maxWidth:900,
        maxHeight:'85vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #eee', display:'flex',
          justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>📜 백업/복구 히스토리</h3>
            <p style={{ margin:'6px 0 0', fontSize:12, color:'#888' }}>
              사용자가 직접 실행한 백업/복구 기록만 표시합니다(매일 새벽 자동 실행되는 시스템
              백업은 제외). 필요 없는 항목은 삭제로 정리할 수 있습니다.
            </p>
          </div>
          <button onClick={onClose}
            style={{ background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              padding:'8px 14px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            닫기
          </button>
        </div>
        <div style={{ padding:'12px 20px', overflow:'auto', flex:1 }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={cellHead}>실행</th>
                <th style={cellHead}>일자</th>
                <th style={cellHead}>시간</th>
                <th style={cellHead}>실행자</th>
                <th style={cellHead}>항목</th>
                <th style={cellHead}>삭제</th>
              </tr>
            </thead>
            <tbody>
              {manualLogs.length === 0 && (
                <tr><td colSpan={6} style={{ ...cellBody, padding:'28px 8px', color:'#999' }}>
                  수동으로 실행한 백업/복구 이력이 없습니다.
                </td></tr>
              )}
              {manualLogs.map(row => {
                const { date, time } = splitDT(row.created_at);
                return (
                  <tr key={row.id} style={{ background:'#fff' }}>
                    <td style={cellBody}>{execLabel(row)}</td>
                    <td style={cellBody}>{date}</td>
                    <td style={cellBody}>{time}</td>
                    <td style={cellBody}>{row.created_by || '-'}</td>
                    <td style={cellBody}>
                      <button onClick={() => setItemsRow(row)}
                        style={{ background:'#fff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:6,
                          padding:'6px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        항목 보기 ({row.menus.length})
                      </button>
                    </td>
                    <td style={cellBody}>
                      <button onClick={() => onDelete(row)}
                        title="삭제"
                        style={{ background:'#fff', color:'#dc2626', border:'1px solid #f5c2c2', borderRadius:6,
                          padding:'6px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        🗑
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {itemsRow && (
        <ItemsViewModal
          row={itemsRow}
          menus={menus}
          execLabel={execLabel}
          splitDT={splitDT}
          onClose={() => setItemsRow(null)}
        />
      )}
    </div>
  );
}

// 2026-08 신설 — 히스토리의 "항목" 버튼을 눌렀을 때, 그 백업/복구 건에 실제로 포함됐던
// 메뉴 목록만 골라 보여주는 모달(전체 메뉴에 O/X를 다 나열하지 않고 포함된 것만 보여준다).
function ItemsViewModal({ row, menus, execLabel, splitDT, onClose }) {
  const { date, time } = splitDT(row.created_at);
  const included = menus.filter(m => row.menus.includes(m.key));
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1100,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, width:360, maxWidth:'90vw',
        maxHeight:'80vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #eee' }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>{execLabel(row)} 항목</h3>
          <p style={{ margin:'6px 0 0', fontSize:12, color:'#888' }}>{date} {time}</p>
        </div>
        <div style={{ padding:'14px 20px', overflowY:'auto', flex:1 }}>
          {included.length === 0 && (
            <div style={{ fontSize:13, color:'#999' }}>포함된 항목이 없습니다.</div>
          )}
          {included.map(m => (
            <div key={m.key} style={{ padding:'6px 2px', fontSize:14, color:'#185FA5' }}>
              ✔ {m.label}
            </div>
          ))}
        </div>
        <div style={{ padding:'14px 20px', borderTop:'1px solid #eee', display:'flex', justifyContent:'flex-end' }}>
          <button onClick={onClose}
            style={{ background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// 메뉴 체크박스 선택 모달 (백업/복구 공용)
function MenuSelectModal({ title, desc, menus, selected, onToggle, onSelectAll, onClose, onConfirm, confirmLabel, confirmColor, loading }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, width:420, maxWidth:'92vw',
        maxHeight:'80vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #eee' }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>{title}</h3>
          <p style={{ margin:'6px 0 0', fontSize:12, color:'#888', lineHeight:1.5 }}>{desc}</p>
        </div>
        <div style={{ padding:'12px 20px', overflowY:'auto', flex:1 }}>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
            <button onClick={onSelectAll}
              style={{ background:'none', border:'none', color:'#185FA5', fontSize:12,
                fontWeight:600, cursor:'pointer', padding:0 }}>
              전체 선택
            </button>
          </div>
          {menus.map(m => (
            <label key={m.key} style={{ display:'flex', alignItems:'center', gap:8,
              padding:'10px 3px', fontSize:14, cursor:'pointer' }}>
              <input type="checkbox" checked={selected.includes(m.key)} onChange={() => onToggle(m.key)} />
              {m.label}
            </label>
          ))}
          {menus.length === 0 && (
            <div style={{ fontSize:13, color:'#999', padding:'12px 0' }}>선택 가능한 메뉴가 없습니다.</div>
          )}
        </div>
        <div style={{ padding:'14px 20px', borderTop:'1px solid #eee', display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button onClick={onClose}
            style={{ background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            취소
          </button>
          <button onClick={onConfirm} disabled={loading}
            style={{ background: confirmColor || '#185FA5', color:'#fff', border:'none', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor: loading?'default':'pointer',
              opacity: loading?0.6:1 }}>
            {loading ? '처리 중...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
